import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { ChartBar, CursorClick, Bell, Prohibit } from "@phosphor-icons/react";

/**
 * PushAnalyticsCard — Phase E. Admin-only dashboard tile showing the
 * push-notification funnel:
 *
 *   delivered → clicked → returned (1–7d later)
 *
 * Sections:
 *   - Two stat columns: 7-day vs 30-day windows. Each shows total
 *     delivered, clicked, and CTR.
 *   - Per-type breakdown table inside each window so we can see whether
 *     daily_summary or truck_live drives most clicks.
 *   - Block-reason pie (last 7d) — quiet_hours / daily_cap / dedup / etc.
 *   - Return-rate stat (30d cohort, returned 1–7d after click).
 *   - Recent feed (last 15 deliveries).
 *
 * The endpoint returns deterministic empty arrays/zeros when there are no
 * rows, so we just render bind-and-paint with no loading skeleton tricks.
 */
const TYPE_LABELS = {
  daily_summary: "Daily summary",
  truck_live: "Truck goes live",
  high_activity: "Big lunch days",
  broadcast: "Manual broadcast",
  admin_test: "Admin test",
};

const REASON_LABELS = {
  quiet_hours: "Quiet hours",
  daily_cap: "Daily cap (3/day)",
  dedup: "Dedup window (2h)",
  master_disabled: "User master off",
  opt_out_daily_summary: "Opted out: daily",
  opt_out_live_alerts: "Opted out: live",
  opt_out_high_activity: "Opted out: high-activity",
  inactive_user: "Inactive user",
  stale_event: "Stale event",
  no_active_subscription: "No active sub",
  anonymous_subscription: "Anon sub",
};

function pct(n) {
  return `${(n * 100).toFixed(1)}%`;
}

function fmtTimeAgo(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  const diff = Date.now() - dt.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function PushAnalyticsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get("/admin/push/analytics")
      .then(({ data }) => {
        if (mounted) setData(data);
      })
      .catch((e) => {
        if (mounted) toast.error(formatApiError(e) || "Couldn't load push analytics");
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <section className="rounded-2xl border border-border bg-card p-5" data-testid="admin-push-analytics">
        <h2 className="font-display font-bold text-base">Push performance</h2>
        <p className="text-xs text-muted-foreground mt-1">Loading…</p>
      </section>
    );
  }
  if (!data) return null;

  const w7 = data.windows?.["7d"] || { delivered: 0, clicked: 0, ctr: 0, by_type: [] };
  const w30 = data.windows?.["30d"] || { delivered: 0, clicked: 0, ctr: 0, by_type: [] };
  const blocked = data.blocked_7d || {};
  const blockedTotal = Object.values(blocked).reduce((a, b) => a + b, 0);
  const rr = data.return_rate_30d || { cohort: 0, returned: 0, rate: 0 };
  const recent = data.recent || [];

  return (
    <section className="rounded-2xl border border-border bg-card p-5 space-y-5" data-testid="admin-push-analytics">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display font-bold text-base inline-flex items-center gap-2">
            <ChartBar weight="duotone" className="h-5 w-5 text-primary" />
            Push performance
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Delivery, click-through, and return rate for the notifications system.
          </p>
        </div>
      </header>

      {/* Two-column window comparison */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="push-windows">
        {[
          { label: "Last 7 days", w: w7, key: "7d" },
          { label: "Last 30 days", w: w30, key: "30d" },
        ].map(({ label, w, key }) => (
          <div key={key} className="rounded-xl border border-border bg-muted/10 p-3" data-testid={`push-window-${key}`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
            <div className="mt-1.5 grid grid-cols-3 gap-2">
              <Stat label="Delivered" value={w.delivered} icon={<Bell weight="duotone" className="h-3.5 w-3.5" />} />
              <Stat label="Clicked" value={w.clicked} icon={<CursorClick weight="duotone" className="h-3.5 w-3.5" />} />
              <Stat label="CTR" value={pct(w.ctr || 0)} />
            </div>
            {w.by_type?.length > 0 && (
              <table className="mt-3 w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium pb-1">Type</th>
                    <th className="text-right font-medium pb-1">Sent</th>
                    <th className="text-right font-medium pb-1">Clicked</th>
                    <th className="text-right font-medium pb-1">CTR</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {w.by_type.map((row) => (
                    <tr key={row.type}>
                      <td className="py-1 pr-2">{TYPE_LABELS[row.type] || row.type}</td>
                      <td className="py-1 text-right tabular-nums">{row.delivered}</td>
                      <td className="py-1 text-right tabular-nums">{row.clicked}</td>
                      <td className="py-1 text-right tabular-nums font-semibold">{pct(row.ctr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>

      {/* Return rate */}
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="push-return-rate">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-300">
              7-day return rate · last 30d clickers
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Of users who clicked any push, what fraction came back to the site within 1–7 days.
            </p>
          </div>
          <div className="text-right">
            <div className="font-display text-2xl font-bold tabular-nums">{pct(rr.rate || 0)}</div>
            <div className="text-[10px] text-muted-foreground tabular-nums">
              {rr.returned} of {rr.cohort} clickers
            </div>
          </div>
        </div>
      </div>

      {/* Block-reason breakdown */}
      <div data-testid="push-blocked-reasons">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground inline-flex items-center gap-1.5">
          <Prohibit weight="duotone" className="h-3.5 w-3.5" />
          Suppressed by rules · last 7d
          {blockedTotal > 0 && <span className="text-foreground tabular-nums">({blockedTotal})</span>}
        </div>
        {blockedTotal === 0 ? (
          <p className="mt-1 text-xs text-muted-foreground">No suppressions in the last 7 days — quiet engine.</p>
        ) : (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(blocked)
              .sort(([, a], [, b]) => b - a)
              .map(([reason, n]) => (
                <span
                  key={reason}
                  className="inline-flex items-center gap-1 text-[11px] rounded-full bg-muted/40 border border-border px-2 py-1"
                  data-testid={`push-blocked-${reason}`}
                >
                  <span className="font-semibold tabular-nums">{n}</span>
                  <span className="text-muted-foreground">{REASON_LABELS[reason] || reason}</span>
                </span>
              ))}
          </div>
        )}
      </div>

      {/* Recent feed */}
      {recent.length > 0 && (
        <div data-testid="push-recent-feed">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
            Recent deliveries
          </div>
          <div className="mt-2 space-y-1">
            {recent.map((r) => (
              <div
                key={r.id}
                className="flex items-center gap-2 text-xs rounded-lg border border-border bg-card/50 px-2.5 py-1.5"
              >
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full flex-none ${
                    r.clicked ? "bg-emerald-500" : "bg-muted-foreground/40"
                  }`}
                />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground flex-none w-20 truncate">
                  {TYPE_LABELS[r.type] || r.type}
                </span>
                <span className="font-semibold truncate flex-1 min-w-0">{r.title || "—"}</span>
                <span className="text-muted-foreground tabular-nums flex-none">{fmtTimeAgo(r.sent_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="rounded-lg bg-card/70 border border-border px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="mt-0.5 font-display font-bold text-lg leading-none tabular-nums">{value}</div>
    </div>
  );
}
